/**
 * Phase 22 — Admin Partner Activity & Preferred Partner controls.
 *
 * Route: /admin/partners/activity-controls
 *
 * PINNED USER RULE (Feb 2026):
 *   Admin-only. Rankings NEVER auto-update these controls.
 *   No emails / SMS / notifications / public pages.
 *   No commission / pricing / payout / white-label / historical booking
 *   / report row is mutated by this screen.
 *   Each change is audit-logged with previous & new values + reason.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, AlertTriangle, Loader2, ShieldAlert,
  Star, Save, Search, History, Info,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";


// ── Partner picker (searchable list) ────────────────────────────────────
function PartnerPicker({ partners, partnerId, onChange }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return partners.slice(0, 50);
    return partners.filter((p) =>
      (p.business_name || "").toLowerCase().includes(q) ||
      (p.partner_slug || "").toLowerCase().includes(q) ||
      (p.franchise_region || "").toLowerCase().includes(q),
    ).slice(0, 50);
  }, [partners, query]);

  return (
    <section
      data-testid="ph22-partner-picker"
      className="rounded-2xl bg-white border border-slate-200 p-4 mt-4"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-2">
        <Search className="w-3.5 h-3.5 text-cyan-700" /> Select PWS partner
      </div>
      <input
        data-testid="ph22-partner-search"
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by name, slug or region…"
        className="text-sm w-full border border-slate-200 rounded-md px-2 py-2 mb-2"
      />
      <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
        {filtered.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`ph22-partner-option-${p.id}`}
            onClick={() => onChange(p.id)}
            className={`w-full text-left px-2 py-1.5 hover:bg-slate-50 ${
              partnerId === p.id ? "bg-cyan-50 border-l-2 border-cyan-500" : ""
            }`}
          >
            <div className="font-semibold text-slate-800 text-xs">
              {p.business_name || "—"}{" "}
              <span className="text-[10px] text-slate-500 font-mono">
                {p.partner_slug || ""}
              </span>
            </div>
            <div className="text-[10px] text-slate-500">
              {p.franchise_region || "—"} · {p.pws_partner_status}
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="text-xs text-slate-400 italic px-2 py-3">
            No partners match.
          </div>
        )}
      </div>
    </section>
  );
}


function BoolToggle({ label, testId, value, onChange, warning }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
      <button
        type="button"
        data-testid={testId}
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          value ? "bg-emerald-500" : "bg-slate-300"
        }`}
        role="switch"
        aria-checked={value}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
            value ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </button>
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        {warning && !value && (
          <div className="text-[10px] text-amber-700 mt-0.5 flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>{warning}</span>
          </div>
        )}
      </div>
    </div>
  );
}


function ControlsForm({ controls, catalog, onSave, saving }) {
  const [draft, setDraft] = useState(controls);
  const [reason, setReason] = useState("");

  useEffect(() => {
    setDraft(controls);
    setReason("");
  }, [controls]);

  const set = useCallback((key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const dirty = useMemo(() => {
    return Object.keys(draft).some((k) => draft[k] !== controls[k]);
  }, [draft, controls]);

  const onSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!dirty) {
      toast.error("No changes to save");
      return;
    }
    const patch = {};
    for (const f of catalog.editable_fields) {
      if (draft[f] !== controls[f]) patch[f] = draft[f];
    }
    if (reason.trim()) patch.reason = reason.trim();
    await onSave(patch);
  }, [dirty, draft, controls, catalog, reason, onSave]);

  return (
    <form
      data-testid="ph22-controls-form"
      onSubmit={onSubmit}
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-3">
        Activity controls
      </div>

      {/* Status picker */}
      <div className="mb-3">
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          PWS partner status
        </label>
        <select
          data-testid="ph22-input-pws_partner_status"
          value={draft.pws_partner_status || ""}
          onChange={(e) => set("pws_partner_status", e.target.value)}
          className="text-sm w-full border border-slate-200 rounded-md px-2 py-2"
        >
          {catalog.status_choices.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {(draft.pws_partner_status === "paused"
          || draft.pws_partner_status === "suspended"
          || draft.pws_partner_status === "inactive") && (
          <div data-testid="ph22-status-warning" className="text-[10px] text-amber-700 mt-1 flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>
              {draft.pws_partner_status} partners do not receive new
              public lead allocations. Existing bookings &amp; commission
              records are unchanged.
            </span>
          </div>
        )}
      </div>

      <BoolToggle
        label="Receive public leads"
        testId="ph22-toggle-receive_public_leads"
        value={!!draft.receive_public_leads}
        onChange={(v) => set("receive_public_leads", v)}
        warning="When OFF, this partner will not be considered for any new public-lead allocation."
      />
      <BoolToggle
        label="Region allocation enabled"
        testId="ph22-toggle-region_allocation_enabled"
        value={draft.region_allocation_enabled !== false}
        onChange={(v) => set("region_allocation_enabled", v)}
        warning="When OFF, this partner will not be picked by automatic region-match auto-allocation."
      />
      <BoolToggle
        label="Preferred partner (internal, admin-only)"
        testId="ph22-toggle-preferred_partner"
        value={!!draft.preferred_partner}
        onChange={(v) => set("preferred_partner", v)}
      />

      {draft.preferred_partner && (
        <div className="mb-3">
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Preferred partner reason
          </label>
          <input
            data-testid="ph22-input-preferred_partner_reason"
            type="text"
            maxLength={400}
            value={draft.preferred_partner_reason || ""}
            onChange={(e) => set("preferred_partner_reason", e.target.value)}
            placeholder="e.g. consistent service, strong follow-up cadence"
            className="text-sm w-full border border-slate-200 rounded-md px-2 py-2"
          />
        </div>
      )}

      <BoolToggle
        label="Manual review required"
        testId="ph22-toggle-manual_review_required"
        value={!!draft.manual_review_required}
        onChange={(v) => set("manual_review_required", v)}
      />

      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Partner activity note (admin-only)
        </label>
        <textarea
          data-testid="ph22-input-partner_activity_note"
          rows={2}
          maxLength={1000}
          value={draft.partner_activity_note || ""}
          onChange={(e) => set("partner_activity_note", e.target.value)}
          placeholder="Internal note about this partner's activity / capacity / context"
          className="text-sm w-full border border-slate-200 rounded-md px-2 py-2"
        />
      </div>

      <div className="mt-4 border-t border-slate-100 pt-3">
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Reason for this change (logged in audit)
        </label>
        <textarea
          data-testid="ph22-input-reason"
          rows={2}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. franchisee on leave Aug 2026 — pause public allocation"
          className="text-sm w-full border border-slate-200 rounded-md px-2 py-2"
        />
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] text-slate-400 italic flex items-center gap-1">
          <Info className="w-3 h-3" />
          Saved changes write a row to the activity audit log.
        </div>
        <button
          type="submit"
          disabled={!dirty || saving}
          data-testid="ph22-save-btn"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
            dirty && !saving
              ? "bg-cyan-700 text-white hover:bg-cyan-800"
              : "bg-slate-200 text-slate-500 cursor-not-allowed"
          }`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}


function CurrentStateCard({ controls }) {
  return (
    <section
      data-testid="ph22-current-state"
      className="rounded-2xl bg-slate-50 border border-slate-200 p-4 mt-4"
    >
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-2">
        Current state
      </div>
      <ul className="text-xs text-slate-700 space-y-1">
        <li><b>Status:</b> <span data-testid="ph22-current-status">{controls.pws_partner_status}</span></li>
        <li><b>Receives public leads:</b> {controls.receive_public_leads ? "Yes" : "No"}</li>
        <li><b>Region allocation enabled:</b> {controls.region_allocation_enabled !== false ? "Yes" : "No"}</li>
        <li><b>Preferred partner:</b>{" "}
          <span data-testid="ph22-current-preferred" className={controls.preferred_partner ? "font-bold text-amber-700" : ""}>
            {controls.preferred_partner ? <><Star className="inline w-3 h-3 fill-current" /> Yes</> : "No"}
          </span>
          {controls.preferred_partner && controls.preferred_partner_since && (
            <span className="text-[10px] text-slate-500"> · since {controls.preferred_partner_since.slice(0, 10)}</span>
          )}
        </li>
        <li><b>Manual review required:</b> {controls.manual_review_required ? "Yes" : "No"}</li>
        <li>
          <b>White label enabled (read-only):</b> {controls.white_label_enabled ? "Yes" : "No"}
          <span
            className="ml-1 text-[10px] text-slate-500 italic"
            data-testid="ph22-white-label-scope-hint"
          >
            — applies to the customer Summary Report only; the COA always stays SCWT / Aware-branded.
          </span>
        </li>
        <li><b>Commission enabled (read-only):</b> {controls.commission_enabled ? "Yes" : "No"}</li>
        {controls.activity_control_updated_at && (
          <li className="text-[10px] text-slate-500 italic mt-1">
            Last changed: {controls.activity_control_updated_at.slice(0, 19).replace("T", " ")} by{" "}
            {controls.activity_control_updated_by || "—"}
          </li>
        )}
      </ul>
    </section>
  );
}


function AuditList({ rows }) {
  return (
    <section
      data-testid="ph22-audit-list"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-2 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5 text-slate-700" /> Recent activity audit
      </div>
      {rows.length === 0 && (
        <div className="text-xs text-slate-400 italic">No audit entries yet.</div>
      )}
      <ul className="divide-y divide-slate-100">
        {rows.map((row, i) => (
          <li key={i} data-testid={`ph22-audit-row-${i}`} className="py-2 text-xs">
            <div className="font-semibold text-slate-800">
              {row.changed_at.slice(0, 19).replace("T", " ")} ·{" "}
              <span className="text-slate-500 font-normal">{row.changed_by}</span>
            </div>
            <div className="mt-0.5 text-[10px] text-slate-500">
              Source: <span className="font-mono">{row.source}</span>
              {row.reason && (
                <> · Reason: <span className="text-slate-700">{row.reason}</span></>
              )}
            </div>
            <ul className="mt-1 space-y-0.5">
              {Object.entries(row.changes || {}).map(([f, c]) => (
                <li key={f} className="text-[11px] text-slate-700">
                  <span className="font-semibold">{f}</span>:{" "}
                  <span className="line-through text-slate-400">{JSON.stringify(c.from)}</span>{" "}
                  → <span className="text-emerald-700">{JSON.stringify(c.to)}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}


// ── Page ────────────────────────────────────────────────────────────────
export default function AdminPWSPartnerActivityControls() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState(null);
  const [body, setBody] = useState(null);
  const [bodyLoading, setBodyLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load full partner list on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/pws-partners?limit=500");
        if (!alive) return;
        const items = (data.items || []).filter((p) => p.partner_type === "pws_franchisee");
        setPartners(items);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Fetch controls when partner changes.
  useEffect(() => {
    setBody(null);
    if (!partnerId) return;
    let alive = true;
    (async () => {
      setBodyLoading(true);
      try {
        const { data } = await api.get(`/admin/pws-partner-activity/${partnerId}`);
        if (alive) setBody(data);
      } catch (e) {
        if (alive) {
          toast.error(formatApiError(e) || "Failed to load activity controls");
          setBody(null);
        }
      } finally {
        if (alive) setBodyLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [partnerId]);

  const onSave = useCallback(async (patch) => {
    if (!partnerId) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/pws-partner-activity/${partnerId}`, patch);
      setBody({ ...data, recent_audit: data.recent_audit ?? body?.recent_audit ?? [] });
      // Re-fetch audit + controls in full so the audit list is fresh.
      const { data: refreshed } = await api.get(`/admin/pws-partner-activity/${partnerId}`);
      setBody(refreshed);
      const changes = Object.keys(data.changes || {});
      toast.success(`Saved — ${changes.length} field${changes.length === 1 ? "" : "s"} changed`);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [partnerId, body]);

  return (
    <PortalShell>
      <div
        data-testid="admin-pws-partner-activity-page"
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Link
          to="/admin/partners/network"
          data-testid="ph22-back-to-network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
              Aware Partner Network · Phase 22
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Partner Activity &amp; Preferred Partner controls
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              Admin-only controls for partner status, region allocation
              eligibility, public lead allocation and preferred partner
              flagging. Every change is audit-logged with the previous
              and new values plus your reason.
            </p>
          </div>
        </div>

        {/* Safety banner */}
        <div
          data-testid="ph22-safety-banner"
          className="mt-5 rounded-xl bg-cyan-50 border border-cyan-200 p-4 flex items-start gap-3 text-sm text-cyan-900"
        >
          <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Admin-only controls.</strong> Rankings DO NOT
            automatically change these flags. Commission, pricing,
            payouts, white-label settings, historical bookings and
            existing reports are never modified by this screen. No
            emails / SMS / public pages / customer notifications are
            sent.
          </div>
        </div>

        {loading ? (
          <div
            data-testid="ph22-loading"
            className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
          >
            <Loader2 className="w-5 h-5 animate-spin" /> Loading partners…
          </div>
        ) : error ? (
          <div
            data-testid="ph22-error"
            className="mt-8 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-800"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed to load
            </div>
            <div className="text-xs">{error}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 mt-2">
            <PartnerPicker
              partners={partners}
              partnerId={partnerId}
              onChange={setPartnerId}
            />
            <div>
              {!partnerId && (
                <div
                  data-testid="ph22-empty-state"
                  className="rounded-2xl bg-white border border-dashed border-slate-200 p-10 text-center text-slate-400 mt-4"
                >
                  Select a PWS partner on the left to view & edit their
                  activity controls.
                </div>
              )}
              {partnerId && bodyLoading && (
                <div
                  data-testid="ph22-body-loading"
                  className="rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center mt-4"
                >
                  <Loader2 className="w-5 h-5 animate-spin" /> Loading controls…
                </div>
              )}
              {partnerId && !bodyLoading && body && (
                <>
                  <section
                    data-testid="ph22-partner-header"
                    className="rounded-2xl bg-white border border-slate-200 p-4 mt-4"
                  >
                    <div className="font-bold text-slate-800">
                      {body.business_name || "—"}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      {body.partner_slug || "—"} · {body.franchise_region || "—"} · {body.state || "—"}
                    </div>
                  </section>
                  <CurrentStateCard controls={body.controls} />
                  <ControlsForm
                    controls={body.controls}
                    catalog={body.catalog}
                    onSave={onSave}
                    saving={saving}
                  />
                  <AuditList rows={body.recent_audit || []} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
